import { useState, useContext, useEffect } from "react";
import { LanguageContext } from "../context/LanguageProvider";
import Premium from "./Premium";
import { useRef } from "react";
import Modal from '../components/Modal';
import useModal from '../hooks/useModal';
import DatePicker from 'react-datepicker';
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import enUS from 'date-fns/locale/en-US'; // English
import fr from 'date-fns/locale/fr'; // French
import useNavigateToQuestion from '../hooks/useNavigateToQuestion';
import AdditionalInfoIcon from '../components/AdditionalInfoIcon';
import { MaskedInput } from '../components/masks/MaskedDateInput';
import { MaskedInputYearMonth } from '../components/masks/MaskedInputYearMonth';
import { MaskedPhoneInput } from '../components/masks/MaskedPhone';
import { MaskedSinInput } from '../components/masks/MaskedSinInput';


import {
  calculateAge,
  formatDateAsUTC,
  formatDateForAPI,
  formatPhoneNumber,
  customRound,
  parseDateStringToValidDate,
  formatCurrency,
  stripHtmlTags,
  isDebugMode
} from '../utils/utils';

const Questions = ({
  validationErrors,
  inputRefs,
  setQuestions,
  handleInputBlur,
  subArea,
  firstLevelArea,
  secondLevelArea,
  client,
  product,
  hasValidated,
  inputBlurTriggered,
  savingQuestionId,
  question,
  setIsDataSaved,
  setFirstLevelArea,
  setSubArea,
  setSecondLevelArea,
  setHasNext,
  setHasPrevious,
  setPreviousFirstLevelArea,
  setPreviousSecondLevelArea,
  setPreviousSubArea,
  setGoToResults
}) => {

  const { t, language } = useContext(LanguageContext);

  const navigateToQuestion = useNavigateToQuestion();

  const handleNavigateToQuestion = async (questionCode, uiRenderType, compositionType) => {

    if (uiRenderType === "MOVE_RETURN_SIMPLE_TEXT_VALUE" || uiRenderType === "MOVE_RETURN") {
      setGoToResults(true);
    }

    await navigateToQuestion(
      product,
      client,
      compositionType,
      questionCode,
      setFirstLevelArea,
      setSubArea,
      setSecondLevelArea,
      setHasNext,
      setHasPrevious,
      setPreviousFirstLevelArea,
      setPreviousSecondLevelArea,
      setPreviousSubArea,
      firstLevelArea,
      secondLevelArea,
      subArea,
      uiRenderType,
      inputRefs,     
    );
  };



  //Modal
  const { isModalVisible, modalContent, openModal, closeModal } = useModal();

  const [isDropdownVisible, setIsDropdownVisible] = useState(true);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const inputRef = useRef(null);
  const [fadeClass, setFadeClass] = useState('');
  const [removeDropAfter, setRemoveDropAfter] = useState('');
  const [selectedValue, setSelectedValue] = useState(question.value || "");

  const [inputKey, setInputKey] = useState(0); // Key for datepicker to close it on save

   const datePickerRef = useRef(null);


  const [inputValue, setInputValue] = useState(
    question.value !== undefined && question.value !== null ? question.value.toString() : ""
  );

  // Register the locales
  registerLocale('en-US', enUS);
  registerLocale('fr', fr);

  // Convert language to locale identifier
  const getLocale = (language) => {
    switch (language) {
      case 'Français':
        return 'fr';
      case 'English':
        return 'en-US';
      default:
        return 'en-US'; // Default to English if language is unrecognized
    }
  };

  // Determine the locale based on the language
  const locale = getLocale(language);

  const [age, setAge] = useState(() => {
    const dv = typeof question.value === 'string' && question.value ? question.value.split("T")[0] : "";
    return dv ? calculateAge(new Date(dv)) : null;
  });

  const today = new Date();


  const eighteenYearsAgo = new Date(new Date().setFullYear(today.getFullYear() - 18));

  const seventyYearsAgo = new Date(new Date().setFullYear(today.getFullYear() - 70));

  const seventyYearsMinusOneDay = new Date(seventyYearsAgo.setDate(seventyYearsAgo.getDate() + 1));


  const initialDate = (() => {
    if (typeof question.value === 'string' && question.value) {
      // Parse the date string without time shift
      const rawDate = new Date(`${question.value.split("T")[0]}T00:00:00`);
      const utcOffset = new Date().getTimezoneOffset(); // UTC offset in minutes

      if (utcOffset > 0) {
        rawDate.setDate(rawDate.getDate());
      }

      return rawDate;
    }

    return null; // Return null if no valid date is provided
  })();

  const [selectedDate, setSelectedDate] = useState(initialDate) || null;
  const [inputModified, setInputModified] = useState(false);




  const handleClear = () => {
    setSelectedDate(null);
    setAge(null);
    handleInputBlur({ ...question, value: null });
    setInputModified(false);
  };

  const handlePhysiciandate = (input = null) => {

    setInputModified(true); // Mark input as modified
    if (input === null || !input) {
      setSelectedDate(null);

      handleInputBlur({ ...question, value: null });
    } else if (input instanceof Date) {
      if (input === "____-__") {
        handleInputBlur({ ...question, value: null });
      } else {
        setSelectedDate(input);
        const formattedDate = formatDateForAPI(input);
        handleInputBlur({ ...question, value: formattedDate });
      }
    }
  };

  const handleDateChange = (input = null) => {

    setInputModified(true); // Mark input as modified
    if (input === null || !input) {
      setSelectedDate(null);
      setAge(null);
      handleInputBlur({ ...question, value: null });
    } else if (input instanceof Date) {
      if (input === "____-__-__") {
        handleInputBlur({ ...question, value: null });
      } else {
        setSelectedDate(input);
        setAge(calculateAge(input));
        const formattedDate = formatDateForAPI(input);
        handleInputBlur({ ...question, value: formattedDate });
      }
    }
  };

  // Logic for "Add custom amount"

  const toggleToInput = () => {
    // Start exit animation
    setFadeClass('fade-exit-active');
    setRemoveDropAfter('removedrop');

    // After exit animation, change state and setup for enter animation
    setTimeout(() => {
      setIsDropdownVisible(false);
      setIsInputVisible(true);

      // Convert question.value to a numeric value for editing
      let initialValue = parseFloat(question.value);
      if (isNaN(initialValue)) {
        initialValue = ''; // Use an empty string if the initial value isn't a number
      } else {
        // Convert back to a string 
        initialValue = initialValue.toString();
      }
      setInputValue(initialValue); // Set as the input value without currency formatting

      inputRef.current?.focus();
      inputRef.current?.select();
      setFadeClass('fade-enter-active');

      setTimeout(() => inputRef.current?.focus(), 200);
    }, 200);
  };

  const toggleToDropdown = () => {
    setFadeClass('fade-exit-active');
    setRemoveDropAfter('');
    setTimeout(() => {
      setIsInputVisible(false);
      setIsDropdownVisible(true);
      setSelectedValue(question.value);

      setFadeClass('fade-enter-active');
    }, 300);
  };

  const [isModified, setIsModified] = useState(false);

  // Update temporary input value on change
  const handleTextChange = (event) => {
    let newValue = event.target.value;

    setIsModified(true);

    setInputValue(newValue);
    setIsDataSaved(false);

    if (!isModified) {
      setIsModified(true);
    }
  };

  const handleDropdownChange = (event) => {
    const rawValue = event.target.value;
    // Attempt to convert the value to a number
    const newValue = Number(rawValue);

    // Use the converted number if it's a valid number, otherwise fallback to a default
    const numberValue = !isNaN(newValue) ? newValue : 0;

    setSelectedValue(numberValue);
    handleInputBlur({ ...question, value: numberValue });
  };

  // Input text change handler for custom amount
  const handleInputChange = (event) => {
    let rawValue = event.target.value;

    // Reseet the value if the value is just a $ or starts with $ followed by non-digit characters
    if (rawValue === '$' || rawValue.match(/^\$\D*$/)) {
      setInputValue('');
      return;
    }

    // Remove any character that isn't a digit or a decimal point.
    let sanitizedValue = rawValue.replace(/[^\d.]/g, '');

    // Update the state with the sanitized value
    setInputValue(sanitizedValue);
  };

  const handleInputBlurForCustomAmount = () => {

    // Remove non-numeric characters except for the minus sign and decimal point
    const numericValueStripped = inputValue ? inputValue.replace(/[^0-9.-]+/g, '') : null;
    // Check if the stripped value is not null or empty before parsing
    const numericValue = numericValueStripped ? parseInt(numericValueStripped, 10) : null;
    // Directly set to null if numericValue is NaN, otherwise apply custom rounding
    const roundedValue = numericValue === null || isNaN(numericValue) ? null : customRound(numericValue).toString();

    // Use rounded value (or null if input was invalid or empty) for the update
    handleInputBlur({ ...question, value: roundedValue });
    toggleToDropdown();

  };

  // Keeping input value in sync with question.value
  useEffect(() => {
    if (!isInputVisible) {
      // Sync with the latest value when switching back to dropdown
      setSelectedValue(question.value);
    }
  }, [question.value, isInputVisible]);

  const [isFocused, setIsFocused] = useState(false);

  const [localOverrideValue, setLocalOverrideValue] = useState(null);


  const handleNumericChange = (event) => {
    let value = event.target.value;
    const isFrench = language === "Français";
    const decimalSeparator = isFrench ? "," : ".";

    // While editing, allow both separators temporarily
    const tempDecimalRegex = /[^0-9.,]/g;
    value = value.replace(tempDecimalRegex, "");

    //  Replace the "wrong" separator with the right one
    if (isFrench) {
      value = value.replace(/\./g, ","); // dots → commas
    } else {
      value = value.replace(/,/g, "."); // commas → dots
    }

    //  Enforce only one separator
    const parts = value.split(decimalSeparator);
    if (parts.length > 2) {
      value = parts[0] + decimalSeparator + parts.slice(1).join("");
    }

    //  Limit decimal precision for REAL
    if (question.valueType === "REAL" && parts.length === 2) {
      parts[1] = parts[1].slice(0, 2);
      value = parts.join(decimalSeparator);
    }

    //  For INT: strip all separators
    if (question.valueType === "INT") {
      value = value.replace(/[^0-9]/g, "");
    }

    setInputValue(value);
  };

  // Handle blur: update global state and save changes
  const handleNumericBlur = () => {
    setIsFocused(false);

    let raw = inputValue.replace(" $", "").replace("$", "").trim();

    //  If input is empty
    if (!raw) {
      if (question.value === null || question.value === undefined) return;

      setLocalOverrideValue(null); // Optimistic clear
      handleInputBlur({ ...question, value: null });
      setInputValue("");
      return;
    }

    let parsedValue;

    if (question.valueType === "REAL") {
      parsedValue = parseFloat(raw.replace(",", "."));
      if (!isNaN(parsedValue)) {
        parsedValue = parseFloat(parsedValue.toFixed(2));
      }
    } else {
      parsedValue = parseInt(raw.replace(",", ""), 10);
    }

    if (isNaN(parsedValue)) {
      setLocalOverrideValue(null); // Prevent ghost value if NaN
      handleInputBlur({ ...question, value: null });
      setInputValue("");
      return;
    }

    //  If value hasn't changed, skip PUT
    if (parsedValue === question.value) return;

    //  Optimistic update — prevents flicker
    setLocalOverrideValue(parsedValue);

    handleInputBlur({ ...question, value: parsedValue });

    // Format value for immediate display
    setInputValue(formatDisplayValue(parsedValue));
  };

  const formatDisplayValue = (value) => {
    if (value === null || value === undefined || value === "") return "";

    if (question.valueType === "TEXT") {
      return value; // Don't format text
    }

    const isFrench = language === "Français";
    const locale = isFrench ? "fr-FR" : "en-CA";

    const options = {
      useGrouping: true,
      minimumFractionDigits: question.valueType === "REAL" ? 2 : 0,
      maximumFractionDigits: question.valueType === "REAL" ? 2 : 0,
    };

    // Format the number with grouping
    let str = new Intl.NumberFormat(locale, options).format(Number(value));

    // Add $ if it's MONEY
    if (question.uiFormat === "MONEY-REAL" || question.uiFormat === "MONEY-INT") {
      str = isFrench ? `${str} $` : `$${str}`;
    }

    return str;
  };

const handleNumericChangeUnits = (event) => {

  const cleanValue = event.target.value.replace(/[^0-9]/g, ''); // allow only digits
  // Set max digits depending on the question code
  let maxDigits = 3; // default for cm/weight
  if (question.code === 'Q_HEIGHT_FEET') maxDigits = 1;   // feet → max 1 digit
  if (question.code === 'Q_HEIGHT_INCHES') maxDigits = 2; // inches → max 2 digits

  const limitedValue = cleanValue.slice(0, maxDigits);

  setInputValue(limitedValue === '' ? '' : limitedValue);
};


  // Handle blur: update global state and save changes
  const handleBlur = () => {
    const newValue = inputValue.trim();

    //  Optimistically update local override to avoid flicker
    setLocalOverrideValue(newValue !== "" ? newValue : null);

    //  Trigger PUT with final value (or null)
    handleInputBlur({
      ...question,
      value: newValue !== "" ? newValue : null,
    });


    //setInputValue(currentValue); // Update the inputValue to the current value of the question

    setIsModified(false);

    // Blur the textarea element
    const divRef = inputRefs.current[question.uniqueId];
    if (divRef) {
      const inputElement = divRef.querySelector("textarea");
      if (inputElement) {
        inputElement.blur();
      }
    }
  };


  const handleBlurUnits = (event) => {

    const rawValue = event.target.value.trim(); // Trim spaces to avoid false updates

    // Convert question.value to string for reliable comparison (null !== "")
      setInputValue(rawValue);

      handleInputBlur({
        ...question,
        value: rawValue !== '' ? parseInt(rawValue, 10) : null, // Convert properly or send null
      });
   
  };

  // Handle blur for phone
  const handleBlurPhone = () => {



    // If the input is still masked placeholder (meaning user didn't type), treat it as null
    if (inputValue === '' || inputValue === '(___) ___-____') {
      handleInputBlur({
        ...question,
        value: null,
      });

      return;
    }

    const formattedNumber = formatPhoneNumber(inputValue);

    handleInputBlur({
      ...question,
      value: formattedNumber === '' ? null : formattedNumber,
    });
  };


  // Sync inputValue after server save; skip if this is an optimistic local update
  useEffect(() => {
    if (question.value !== localOverrideValue) {
      setInputValue(question.value !== undefined && question.value !== null ? question.value.toString() : "");
      setLocalOverrideValue(null);
    }
  }, [question.value]);



  return (

    <>
      <div id={'go_to_' + question.uniqueId}
        ref={(el) => { inputRefs.current['go_to_' + question.uniqueId] = el; }}
        className={'code_' + question.code + ' uirendertype_' + question.uiRenderType}
        data-question-code={question.code}
      >
        {question.uiRenderType !== "CHECKBOX" &&
          question.uiRenderType !== "COVERAGE_CHECKBOX" &&
          question.uiRenderType !== "PREMIUM_TYPE" &&
          question.uiRenderType !== "EMAIL" &&
          question.uiRenderType !== "HEAD_ITALIC" &&
          question.uiRenderType !== "HEAD_ITALIC_LINE_ABOVE" &&
          question.uiRenderType !== "SIMPLE_TEXT_VALUE" &&
          question.uiRenderType !== "HEIGHT" &&
          question.uiRenderType !== "WEIGHT" &&
          question.uiRenderType !== "MOVE_NEXT" &&
          question.uiRenderType !== "DROPDOWN" &&
          question.uiRenderType !== "MOVE_RETURN" &&
          question.uiRenderType !== "BEN_ADD_REM_FIRST" &&
          question.uiRenderType !== "BEN_ADD_REM" &&
          question.uiRenderType !== "ADDRESS_CUSTOMIZE" &&
          question.uiRenderType !== "MOVE_RETURN_SIMPLE_TEXT_VALUE" &&
          question.uiRenderType !== "ADD_HOLDER" &&
          question.uiRenderType !== "REMOVE_HOLDER" &&
          question.uiRenderType !== "TITLE" ? (

          <div className="label">
            <label
              dangerouslySetInnerHTML={{ __html: question.text }}
              onClick={(e) => {
                // For "IN_LINE" additional info
                if (question.additionalInfo?.uiRenderType === "IN_LINE") {
                  const targetLink = e.target.closest('a[data-info]');
                  if (targetLink) {
                    e.preventDefault();
                    openModal(question.additionalInfo.title, question.additionalInfo.content);
                  }
                }
              }}
            >
            </label>

            <AdditionalInfoIcon additionalInfo={question.additionalInfo} openModal={openModal} />

          </div>
        ) : null}

        {/* Types of titles */}
        {question.uiRenderType === "HEAD_ITALIC" && (
          <div className="head-italic">{question.text}</div>
        )}

        {question.uiRenderType === "HEAD_ITALIC_LINE_ABOVE" && (
          <div className="head-italic line-above">{question.text}</div>
        )}

        {question.uiRenderType === "TITLE" && (
          <div className="title">{question.text}</div>
        )}

        {question.uiRenderType === "SIMPLE_TEXT" && (
          <>
          </>
        )}

        {question.uiRenderType === "SIMPLE_TEXT_VALUE" && (

          <>
            <label
              dangerouslySetInnerHTML={{ __html: question.text }}
              onClick={(e) => {
                // For "IN_LINE" additional info
                if (question.additionalInfo?.uiRenderType === "IN_LINE") {
                  const targetLink = e.target.closest('a[data-info]');
                  if (targetLink) {
                    e.preventDefault();
                    openModal(question.additionalInfo.title, question.additionalInfo.content);
                  }
                }
              }}
            >
            </label>

            <span className="simple-text-value">
              {
                question.possibleValues && Array.isArray(question.possibleValues)
                  ? (() => {
                    const match = question.possibleValues.find(pv => pv.value === question.value);
                    if (match) {
                      return <> : {match.uiValue}</>; 
                    } else {
                      return question.value === null
                        ? <span className="d-inline"> : {t('no')}</span>
                        : `: ${question.value}`;
                    }
                  })()
                  : null
              }
            </span>
          </>

        )}

        {(["BUTTONS", "LANGUAGE"].includes(question.uiRenderType)) && (

          <>
            <div
              className={`check ${validationErrors && validationErrors.some((error) => error.id === question.id) ? "hasError" : ""
                } ${
                // Check if the question ID matches any error ID and if that error's uiRenderType is "VALIDATION_AT_LEAST_ONE"
                validationErrors && validationErrors.some((error) => error.id === question.id && error.additionalInfoDto && error.additionalInfoDto.uiRenderType === "VALIDATION_AT_LEAST_ONE") ? "VALIDATION_AT_LEAST_ONE" : ""
                }`}
              id={question.uniqueId}

            >
              <div id={question.uniqueId}>
                {question.possibleValues && question.possibleValues.length > 0 ? (
                  question.possibleValues.map((possibleValue) => (
                    <div className="form-check radio-binary" key={`possibleValue-${possibleValue.value}`}>
                      <input
                        className="form-check-input"
                        type="radio"
                        id={question.uniqueId + "__" + possibleValue.value}
                        name={question.uniqueId}
                        value={possibleValue.value}

                        disabled={possibleValue.disabled}

                        checked={question.value == possibleValue.value}

                        ref={(el) => {
                          const refId = question.uniqueId;
                          inputRefs.current[refId] = el;
                        }}

                        onChange={(event) => {

                          const newValue = event.target.value;
                          const updatedQuestionValue = { ...question, value: newValue };

                          handleInputBlur(updatedQuestionValue);

                        }}

                      />
                      <label
                        className="form-check-label binary"
                        htmlFor={question.uniqueId + "__" + possibleValue.value}
                      >
                        {possibleValue.uiValue}
                      </label>
                    </div>
                  ))
                ) : (
                  <div style={{ color: "red" }}>No options available</div>
                )}
              </div>
              <div className={`invalid-feedback ${inputRefs.current[question.uniqueId] ? 'd-block' : ''}`}>
                {
                  validationErrors &&
                  validationErrors.find(error => error.id === question.id)?.errorText
                }
              </div>
            </div>
          </>
        )}

        {question.uiRenderType === "BUTTONS_RESULTS" && (
          <>
            <div
              className={`buttons-results check ${validationErrors && validationErrors.some((error) => error.id === question.id) ? "hasError" : ""
                } ${
                // Check if the question ID matches any error ID and if that error's uiRenderType is "VALIDATION_AT_LEAST_ONE"
                validationErrors && validationErrors.some((error) => error.id === question.id && error.additionalInfoDto && error.additionalInfoDto.uiRenderType === "VALIDATION_AT_LEAST_ONE") ? "VALIDATION_AT_LEAST_ONE" : ""
                }`}
              id={question.uniqueId}

            >
              <div id={question.uniqueId}>
                {question.possibleValues && question.possibleValues.length > 0 ? (
                  question.possibleValues.map((possibleValue) => (
                    <div className="form-check radio-binary" key={`possibleValue-${possibleValue.value}`}>
                      <input
                        className="form-check-input"
                        type="radio"
                        id={question.uniqueId + "__" + possibleValue.value}
                        name={question.uniqueId}
                        value={possibleValue.value}
                        checked={question.value == possibleValue.value}

                        onChange={(event) => {
                          const newValue = event.target.value;
                          const updatedQuestionValue = { ...question, value: newValue };

                          handleInputBlur(updatedQuestionValue);


                        }}
                      />

                      <label
                        className="form-check-label binary"
                        htmlFor={question.uniqueId + "__" + possibleValue.value}
                      >
                        {possibleValue.uiValue}
                      </label>
                    </div>
                  ))
                ) : (
                  <div style={{ color: "red" }}>No options available</div>
                )}
              </div>
              <div className={`invalid-feedback ${inputRefs.current[question.uniqueId] ? 'd-block' : ''}`}>
                {
                  validationErrors &&
                  validationErrors.find(error => error.id === question.id)?.errorText
                }
              </div>
            </div>
          </>
        )}

        {(["INPUT_TEXT", "INPUT_NUMBER"].includes(question.uiRenderType)) && (

          <div
            className={`form-group ${validationErrors &&
              validationErrors.some((error) => error.id === question.id)
              ? "hasError"
              : ""
              }`}

            ref={(el) => {
              const refId = question.uniqueId;
              inputRefs.current[refId] = el;
            }}
          >

            <input
              type="text"
              className="form-control"
              id={question.uniqueId}

              value={
                (question.uiFormat === "MONEY-REAL" || question.uiFormat === "MONEY-INT")
                  ? isFocused
                    ? inputValue
                    : formatDisplayValue(
                      localOverrideValue != null ? localOverrideValue : question.value,
                      question.uiFormat
                    )
                  : inputValue
              }

              disabled={question.disabled}

              onChange={(e) => {
                if (question.valueType === "TEXT") {
                  handleTextChange(e);
                } else {
                  handleNumericChange(e);
                }
              }}

              onFocus={() => {
                setIsFocused(true);

                let raw = inputValue
                  .replace(" $", "")
                  .replace("$", "")
                  .trim();

                //  Normalize comma to dot in French so we can edit with real numbers
                if (question.valueType === "REAL" && language === "Français") {
                  raw = raw.replace(",", ".");
                }
                setInputValue(raw);
              }}

              onBlur={(e) => {
                setIsFocused(false);

                if (question.valueType === "TEXT") {
                  handleBlur(e); // TEXT uses plain blur
                } else {
                  handleNumericBlur(); // NUMERIC uses formatted blur
                }
              }}

              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.target.blur();
                }
              }}
            />


            <div
              className={`invalid-feedback ${hasValidated &&
                inputRefs.current[question.uniqueId]
                ? "d-block"
                : ""
                }`}
            >
              {validationErrors &&
                validationErrors.find((error) => error.id === question.id)?.errorText}
            </div>
          </div>
        )}

        {question.uiRenderType === "CONFIRMATION_EMAIL" && (
          <div
            className={`form-group ${validationErrors &&
              validationErrors.some((error) => error.id === question.id)
              ? "hasError"
              : ""
              }`}

            ref={(el) => {
              const refId = question.uniqueId;
              inputRefs.current[refId] = el;
            }}

          >
            <input
              title={question.text}
              placeholder={stripHtmlTags(question.defaultText)}

              className="form-control"
              type="text"
              id={`${question.uniqueId}`}
              value={inputValue}
              onChange={handleTextChange}
              onBlur={handleBlur}

              onCopy={(event) => event.preventDefault()}
              onPaste={(event) => event.preventDefault()}


              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault(); // Prevent the default Enter key behavior (e.g., form submission)
                  //handleBlur(event);
                  event.target.blur();
                }
              }}

              onFocus={() => {
                // setValidationErrors([]);
              }}
            />

            <div
              className={`invalid-feedback ${hasValidated &&
                inputRefs.current[question.uniqueId]
                ? "d-block"
                : ""
                }`}
            >
              {validationErrors &&
                validationErrors.find((error) => error.id === question.id)?.errorText}
            </div>
          </div>
        )}

        {question.uiRenderType === "DROPDOWN" && (

          <>

            <label className="drop-label"
              dangerouslySetInnerHTML={{ __html: question.text }}
              onClick={(e) => {
                // For "IN_LINE" additional info
                if (question.additionalInfo?.uiRenderType === "IN_LINE") {
                  const targetLink = e.target.closest('a[data-info]');
                  if (targetLink) {
                    e.preventDefault();
                    openModal(question.additionalInfo.title, question.additionalInfo.content);
                  }
                }
              }}
            >
            </label>
            <AdditionalInfoIcon additionalInfo={question.additionalInfo} openModal={openModal} />

            <div
              className={`${validationErrors &&
                validationErrors.some((error) => error.id === question.id)
                ? "hasError select"
                : "select"
                }`}
            >

              <div
                className="drop"
                id={`select-${question.id}`}
              >
                <select

                  id={question.uniqueId}
                  className="form-control"
                  value={selectedValue || ""}
                  ref={(el) => {
                    const refId = question.uniqueId;
                    inputRefs.current[refId] = el;
                  }}
                  onChange={handleDropdownChange}
                  disabled={question.disabled}
                >
                  {/* Default option */}
                  {!question.value && question.value !== 0 &&
                    <option value="" className="default-option">
                      {question.defaultText || t('chose')}
                    </option>
                  }
                  {question.possibleValues?.map((possibleValue, index) => (
                    <option value={possibleValue.value} key={index}>
                      {possibleValue.uiValue}
                    </option>
                  ))}
                </select>
              </div>

              <div className={`invalid-feedback ${inputRefs.current[question.uniqueId] ? 'd-block' : ''}`}>
                {
                  validationErrors &&
                  validationErrors.find(error => error.id === question.id)?.errorText
                }
              </div>
            </div>

          </>
        )}

        {["CALENDAR", "CALENDAR_18_70", "CALENDAR_18_120"].includes(question.uiRenderType) && (
          <div
            ref={(el) => {
              const refId = question.uniqueId;
              inputRefs.current[refId] = el;
            }}
          >
            {/* Display result */}
            {question.value && age !== null && (
              <div className="result">
                {parseInt(question.value.split("T")[0].split("-")[0], 10) <= 9999 && (
                  <>
                    {formatDateAsUTC(question.value.split("T")[0], language)} - {age} {t("years")}
                  </>
                )}
              </div>
            )}

            {/* Date Picker */}
            <div
              className={`form-group date-icon ${validationErrors && validationErrors.some((error) => error.id === question.id) ? "hasError" : ""
                }`}
            >
              <DatePicker
                id={`calendar-${question.id}`}
                selected={selectedDate}
                key={inputKey}
                onChange={handleDateChange}
                customInput={
                  <MaskedInput
                    onClear={handleClear}
                    onChange={handleDateChange}
                    handleDateChange={handleDateChange}
                    setKey={setInputKey}
                    questionId={question.id}
                    closeCalendar={() => datePickerRef.current?.setOpen(false)}
                  />
                }

                dateFormat="yyyy-MM-dd"

                onBlur={() => {
                  if (inputModified) {
                    if (!selectedDate) {
                      handleInputBlur({ ...question, value: null });
                    } else {
                      handleDateChange(selectedDate);
                    }
                  }
                  setInputModified(false);
                }}

                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                minDate={
                  question.uiRenderType === "CALENDAR_18_70"
                    ? seventyYearsMinusOneDay
                    : question.uiRenderType === "CALENDAR_18_120"
                      ? null
                      : undefined
                }
                maxDate={
                  question.uiRenderType === "CALENDAR_18_70"
                    ? eighteenYearsAgo
                    : question.uiRenderType === "CALENDAR_18_120"
                      ? eighteenYearsAgo
                      : today
                }
                locale={locale}
              />
              <div className={`invalid-feedback ${inputRefs.current ? "d-block" : ""}`}>
                {validationErrors &&
                  validationErrors.find((error) => error.id === question.id)?.errorText}
              </div>
            </div>
          </div>
        )}

        {question.uiRenderType === "PHYSICIAN_TIME" && (
          <div
            ref={(el) => {
              const refId = question.uniqueId;
              inputRefs.current[refId] = el;
            }}
          >

            <div

              className="physician-wrapper"
              tabIndex="0"

            >
              <div className="physician-date"  >
                <div
                  className={`form-group date-icon ${validationErrors && validationErrors.some((error) => error.id === question.id) ? "hasError" : ""}`}
                >
                  <DatePicker
                    id={`physician-time-${question.id}`}
                    selected={
                      question.value === "1910-01-01T00:00:00"
                        ? null
                        : (selectedDate && !isNaN(new Date(selectedDate)))
                          ? new Date(selectedDate)
                          : null
                    }


                    onChange={(e) => {
                      handlePhysiciandate(e);
                    }}

                    key={inputKey}

                    customInput={
                      <MaskedInputYearMonth
                        onClear={handleClear}
                        onChange={handleDateChange}
                        handleDateChange={handleDateChange}
                        setKey={setInputKey}
                        value={question.value}
                      />
                    }

                    dateFormat="yyyy-MM"

                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const currentValue = e.target.value.replace(/_/g, '').trim();

                        if (currentValue === '--' || currentValue === '____-__') {
                          onClear?.();
                          return;
                        }

                        const parsedDate = parseDateStringToValidDate(currentValue);
                        if (parsedDate) {
                          handleDateChange(parsedDate);
                        } else {
                          onClear?.();
                        }
                        setKey((prevKey) => prevKey + 1);
                      }
                    }}

                    /* onBlur={() => {
                       if (inputModified) {
                         if (!selectedDate) {
                           handleInputBlur({ ...question, value: null });
                         } else {
                           handlePhysiciandate(selectedDate);
                         }
                       }
                       setInputModified(false); // Reset modification tracker
                     }}
                       */

                    showMonthDropdown
                    showYearDropdown
                    showMonthYearPicker
                    minDate={seventyYearsMinusOneDay}
                    dropdownMode="select"
                    locale={locale}

                  />
                  <div className={`invalid-feedback ${inputRef.current ? 'd-block' : ''}`}>
                    {
                      validationErrors &&
                      validationErrors.find(error => error.id === question.id)?.errorText
                    }
                  </div>
                </div>
              </div>
              <div className="physician-check">
                <div className='checkbox'>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={question.uniqueId}
                    name={question.uniqueId + (question.productId ? `-${question.productId}` : '')}
                    disabled={question.disabled}

                    // Considered 'checked' based on the question's current value
                    checked={question.value === "1910-01-01T00:00:00"}
                    ref={(el) => {
                      const refId = question.uniqueId;
                      inputRefs.current[refId] = el;
                    }}
                    onChange={(event) => {
                      const isChecked = event.target.checked;

                      if (isChecked) {
                        setSelectedDate(null);
                        handleInputBlur({ ...question, value: "1910-01-01T00:00:00" });
                      } else {
                        handleInputBlur({ ...question, value: selectedDate });
                      }
                    }}
                  />
                  <label htmlFor={question.uniqueId} className="form-check-label">
                    {t('unknown')}

                  </label>

                </div>
              </div>
            </div>


          </div>
        )}

        {(["CHECKBOX", "COVERAGE_CHECKBOX"].includes(question.uiRenderType)) && (
          <div className={`checkbox ${validationErrors && validationErrors.some((error) => error.id === question.id) ? "hasError" : ""} ${question.uiRenderType === "COVERAGE_CHECKBOX" ? "COVERAGE_CHECKBOX" : ""}`}>
            <input
              className="form-check-input"
              type="checkbox"
              id={question.uniqueId + (question.productId ? `-${question.productId}` : '')}
              name={question.uniqueId + (question.productId ? `-${question.productId}` : '')}

              disabled={question.disabled}
              // Considered 'checked' based on the question's current value and valueType
              checked={
                question.valueType === "INT"
                  ? parseInt(question.value, 10) === 1
                  : question.valueType === "BOOL"
                    ? question.value === true
                    : false
              }
              ref={(el) => {
                const refId = question.uniqueId;
                inputRefs.current[refId] = el;
              }}
              onChange={() => {

                let newValue;
                if (question.valueType === "INT") {
                  // Toggle between 1 and 0 for INT type
                  newValue = parseInt(question.value, 10) === 1 ? 0 : 1;
                } else if (question.valueType === "BOOL") {
                  // Toggle between true and false for BOOL type
                  newValue = question.value === true ? false : true;
                }

                const updatedQuestionValue = {
                  ...question,
                  value: newValue,
                };
                handleInputBlur(updatedQuestionValue);
              }}
            />
            <label htmlFor={question.uniqueId + (question.productId ? `-${question.productId}` : '')}
              className="form-check-label" dangerouslySetInnerHTML={{ __html: question.text }}></label>
            <AdditionalInfoIcon additionalInfo={question.additionalInfo} openModal={openModal} />

            <div className={`invalid-feedback ${inputRefs.current[question.uniqueId] ? 'd-block' : ''}`}>
              {
                validationErrors &&
                validationErrors.find(error => error.id === question.id)?.errorText
              }
            </div>
          </div>
        )}

        {question.uiRenderType === "RADIOBOX" && (
          <div
            className={`radiobox ${validationErrors && validationErrors.some(error => error.id === question.id) ? "hasError" : ""
              }`}
          >
            {question.possibleValues.map((option, index) => (
              <div
                key={index}
                className={`form-check ${question.value === option.value ? 'is-selected' : ''}`}

              >
                <input
                  className="form-check-input"
                  type="radio"
                  name={question.uniqueId}
                  id={`${question.uniqueId}-${option.value}`}
                  value={option.value}
                  disabled={option.disabled}
                  tabIndex="0"

                  checked={question.value === option.value}
                  onChange={() => {
                    const updatedQuestionValue = {
                      ...question,
                      value: option.value,
                    };
                    handleInputBlur(updatedQuestionValue);
                  }}
                  ref={(el) => {
                    const refId = `${question.uniqueId}`;
                    inputRefs.current[refId] = el;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === ' ') {
                      e.preventDefault(); // Prevent scrolling
                      const updatedQuestionValue = {
                        ...question,
                        value: option.value,
                      };
                      handleInputBlur(updatedQuestionValue); // Select option on Spacebar
                    }
                  }}
                />
                <label htmlFor={`${question.uniqueId}-${option.value}`} className="form-check-label"


                  tabIndex="0"
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      const updatedQuestionValue = {
                        ...question,
                        value: option.value,
                      };
                      handleInputBlur(updatedQuestionValue);
                    }
                  }}

                >
                  {option.uiValue}
                </label>
              </div>
            ))}
            <div className={`invalid-feedback ${inputRefs.current[question.uniqueId] ? 'd-block' : ''}`}>
              {validationErrors &&
                validationErrors.find(error => error.id === question.id)?.errorText
              }
            </div>
          </div>
        )}

        {question.uiRenderType === "CUSTOM_AMOUNT" && (
          <div
            className={`${fadeClass} ${removeDropAfter}  ${validationErrors &&
              validationErrors.some((error) => error.id === question.id)
              ? "hasError"
              : ""
              }`}

            ref={(el) => {
              const refId = question.uniqueId;
              inputRefs.current[refId] = el;
            }}

          >
            {isDropdownVisible && (
              <>
                <div className="drop">

                  <select
                    className="form-control"
                    id={`select-field-${question.uniqueId}`}
                    value={selectedValue || ''}
                    onChange={handleDropdownChange}
                  >
                    {!selectedValue && (
                      <option value="" className="default-option">
                        {question.defaultText || t('chose')}
                      </option>
                    )}
                    {question.possibleValues?.map((possibleValue, index) => (
                      <option value={possibleValue.value} key={index}>
                        {possibleValue.uiValue}
                      </option>
                    ))}
                    {selectedValue && !question.possibleValues?.some(pv => pv.value === selectedValue) && (
                      <option value={selectedValue} defaultValue>
                        {`${formatCurrency(selectedValue, language, false, 'MONEY-INT')}`}
                      </option>
                    )}
                  </select>
                </div>

                <div className="custom-links">
                  <a href="#" onClick={(e) => { e.preventDefault(); toggleToInput(); }}>{t('custom_amount')}</a>
                </div>
              </>
            )}

            {isInputVisible && (
              <input
                type="text"
                className="form-control"
                ref={inputRef}
                value={inputValue}
                onChange={handleNumericChange}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    toggleToDropdown(); //  toggle back to dropdown
                    handleInputBlurForCustomAmount();
                  }
                }}
                onBlur={handleInputBlurForCustomAmount}
              />
            )}
            <div
              className={`invalid-feedback ${hasValidated &&
                inputRefs.current[question.uniqueId]
                ? "d-block"
                : ""
                }`}
            >
              {validationErrors &&
                validationErrors.find((error) => error.id === question.id)?.errorText}
            </div>
          </div>
        )}

        {question.uiRenderType === "TERMS" && (

          <>
            <div

              data-toggle="buttons"

              className={` ${validationErrors &&
                validationErrors.some((error) => error.id === question.id)
                ? "btn-group btn-group-toggle terms w-100 d-flex align-items-center justify-content-start hasError"
                : "btn-group btn-group-toggle terms w-100 d-flex align-items-center justify-content-start"
                }`}


              ref={(el) => {
                const refId = question.uniqueId;
                inputRefs.current[refId] = el;
              }}
            >
              {question.possibleValues.map((possibleValue, index) => (
                <div key={`radio-${possibleValue.value}`} style={{ flex: 1 }}>
                  <input
                    type="radio"
                    id={question.uniqueId + "__" + possibleValue.value}
                    name={question.uniqueId + "__" + possibleValue.value}
                    value={possibleValue.value}
                    checked={parseInt(question.value, 10) === parseInt(possibleValue.value, 10)}
                    disabled={possibleValue.disabled}
                    onClick={async (e) => {
  e.preventDefault();
                      const updatedQuestionValue = {
                        ...question,
                        value: possibleValue.value,
                      };
                      await handleInputBlur(updatedQuestionValue);
                    }}
                    readOnly
                  />

                  <label
                    htmlFor={question.uniqueId + "__" + possibleValue.value}
                    className={`btn ${index === 0
                      ? "btn-first"
                      : index === question.possibleValues.length - 1
                        ? "btn-last"
                        : ""
                      } ${parseInt(question.value, 10) === parseInt(possibleValue.value, 10) ? "active" : ""
                      } ${possibleValue.disabled ? "disabled" : ""
                      }`}
                  >
                    <span>{possibleValue.uiValue}</span>

                  </label>

                </div>
              ))}

            </div>


            <div className={`invalid-feedback ${inputRefs.current && inputRefs.current[question.uniqueId] ? 'd-block' : ''}`}>
              {
                validationErrors &&
                validationErrors.find(error => error.id === question.id)?.errorText
              }

            </div>
          </>


        )}

        {question.uiRenderType === "PHONE" && (
          <div
            className={` ${validationErrors &&
              validationErrors.some((error) => error.id === question.id)
              ? "hasError"
              : ""
              }`}

            ref={(el) => {
              const refId = question.uniqueId;
              inputRefs.current[refId] = el;
            }}
          >

            <MaskedPhoneInput
              placeholder={stripHtmlTags(question.defaultText)}
              id={question.uniqueId}
              value={inputValue} // e.g., (222) 222-2222
              onChange={handleInputChange}
              onBlur={handleBlurPhone}
            />

            <div
              className={`invalid-feedback ${hasValidated &&
                inputRefs.current[question.uniqueId]
                ? "d-block"
                : ""
                }`}
            >
              {validationErrors &&
                validationErrors.find((error) => error.id === question.id)?.errorText}
            </div>
          </div>
        )}

        {question.uiRenderType === 'SEARCH_ADDRESS' && (
          <>

            <div
              className={`form-group ${validationErrors &&
                validationErrors.some((error) => error.id === question.id)
                ? "hasError"
                : ""
                }`}
            >
              <input

                title={question.text}
                placeholder={stripHtmlTags(question.defaultText)}
                ref={(el) => {
                  const refId = question.uniqueId;
                  inputRefs.current[refId] = el;
                }}

                className="form-control"
                type="text"
                id={`${question.uniqueId}`}
                value={inputValue}
                onChange={handleTextChange}
                onBlur={() => setInputValue('')}


                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault(); // Prevent the default Enter key behavior (e.g., form submission)
                    //handleBlur();
                    setInputValue('');
                  }
                }}

                onFocus={() => {
                  // setValidationErrors([]);
                }}
              />

              <div
                className={`invalid-feedback ${hasValidated &&
                  inputRefs.current[question.uniqueId]
                  ? "d-block"
                  : ""
                  }`}
              >
                {validationErrors &&
                  validationErrors.find((error) => error.id === question.id)?.errorText}
              </div>
            </div>

          </>
        )}

        {question.uiRenderType === "CLASSES" && (
          <div
            className={`form-group ${validationErrors &&
              validationErrors.some((error) => error.id === question.id)
              ? "hasError"
              : ""
              }`}

            ref={(el) => {
              const refId = question.uniqueId;
              inputRefs.current[refId] = el;
            }}
          >

            <div
              className="classes"
              id={question.uniqueId}
            >
              {question.possibleValues.map((possibleValue, index) => (

                <div
                  className={question.value === possibleValue.value ? 'selected-option form-check-input' : 'option form-check-input'}
                  key={index}
                  htmlFor={`option-${index}`}
                  tabIndex="0"

                 onClick={async (e) => {
  e.preventDefault();
                    const updatedQuestionValue = {
                      ...question,
                      value: possibleValue.value,
                    };

                    handleInputBlur(updatedQuestionValue);
                  }}

                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault(); // Prevent scrolling
                      const updatedQuestionValue = {
                        ...question,
                        value: possibleValue.value,
                      };
                      handleInputBlur(updatedQuestionValue);
                    }
                  }}

                >
                  <input
                    className="form-check-input"
                    type="radio"
                    id={`option-${index}`}
                    name={`question-${question.uniqueId}`}
                    value={possibleValue.value}
                    checked={question.value === possibleValue.value}

                    onChange={() => { }}
                  />
                  <div dangerouslySetInnerHTML={{ __html: possibleValue.uiValue }} /></div>
              ))}
            </div>

            <div
              className={`invalid-feedback ${hasValidated &&
                inputRefs.current[question.uniqueId]
                ? "d-block"
                : ""
                }`}
            >
              {validationErrors &&
                validationErrors.find((error) => error.id === question.id)?.errorText}
            </div>
          </div>
        )}

        {question.uiRenderType === "TEXT_AREA" && (

          <div
            className={`form-group ${validationErrors &&
              validationErrors.some((error) => error.id === question.id)
              ? "hasError"
              : ""
              }`}
          >
            <div className="textarea" id={`${question.uniqueId}`}>

              <textarea
                rows="2"
                placeholder={stripHtmlTags(question.defaultText)}
                className="form-control"
                value={inputValue}
                onChange={handleTextChange}
                ref={(el) => {
                  const refId = question.uniqueId;
                  inputRefs.current[refId] = el;
                }}
              />

              <button
                type="button"
                className={`btn-purple ${!isModified && inputValue ? 'btn-gray' : ''}`}
                onClick={(e) => {
  e.preventDefault();
  handleBlur();
}}

              >

                {!isModified && inputValue ? t('confirmed') : t('confirm')}
              </button>
            </div>

            <div className={`invalid-feedback ${inputRefs.current && inputRefs.current[question.uniqueId] ? 'd-block' : ''}`}>
              {
                validationErrors &&
                validationErrors.find(error => error.id === question.id)?.errorText
              }
            </div>
          </div>
        )}

        {question.uiRenderType === "PREMIUM_TYPE" && (
          <>
            <Premium
              question={question}
              setQuestions={setQuestions}
              inputBlurTriggered={inputBlurTriggered}
              client={client}
              product={question.productId}
              inputRefs={inputRefs}
              validationErrors={validationErrors}
              handleInputBlur={handleInputBlur}
            />
          </>
        )}

        {question.uiRenderType === "SIN" && (
          <div
            className={`form-group ${validationErrors &&
              validationErrors.some((error) => error.id === question.id)
              ? "hasError"
              : ""
              }`}

              ref={(el) => {
              const refId = question.uniqueId;
              inputRefs.current[refId] = el;
            }}
          >
            <MaskedSinInput
              id="sin"
              value={inputValue}
              onChange={handleTextChange}
              onBlur={handleBlur}
            />
            <div
              className={`invalid-feedback ${hasValidated &&
                inputRefs.current[question.uniqueId]
                ? "d-block"
                : ""
                }`}
            >
              {validationErrors &&
                validationErrors.find((error) => error.id === question.id)?.errorText}
            </div>
          </div>
        )}

        {question.uiRenderType === "CORP" && isDebugMode() && (
          <div style={{ fontSize: "1rem", color: "red" }}>
            Value: {
              question.value === null ? "null" :
                typeof question.value === "boolean" ? question.value.toString() :
                  question.value
            }
          </div>
        )}

        {(["WEIGHT_UNIT", "HEIGHT_UNIT"].includes(question.uiRenderType)) && (
          <>
            <div className="units-toggle can-toggle"
              tabIndex="0"
              role="switch"
              aria-checked={question.value === question.possibleValues[0].value}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {  // Toggle with Enter or Spacebar
                  e.preventDefault();  // Prevent default space scrolling
                  const newValue = question.value === question.possibleValues[0].value
                    ? question.possibleValues[1].value
                    : question.possibleValues[0].value;

                  const updatedQuestionValue = {
                    ...question,
                    value: newValue,
                  };
                  handleInputBlur(updatedQuestionValue);
                }
              }}
            >
              <input
                type="checkbox"
                id={question.uniqueId}
                name={question.uniqueId}
                checked={question.value === question.possibleValues[0].value}
                disabled={question.disabled}
                onChange={() => {
                  const newValue = question.value === question.possibleValues[0].value
                    ? question.possibleValues[1].value
                    : question.possibleValues[0].value;

                  const updatedQuestionValue = {
                    ...question,
                    value: newValue,
                  };

                  setTimeout(() => {
                    handleInputBlur(updatedQuestionValue);
                  }, 400);
                }}
              />
              <label htmlFor={question.uniqueId} className="switch">
                <div
                  className="can-toggle__switch"
                  data-checked={question.possibleValues[0].uiValue}
                  data-unchecked={question.possibleValues[1].uiValue}
                ></div>
              </label>
            </div>
          </>
        )}

        {(["HEIGHT_CM", "HEIGHT_FEET", "HEIGHT_INCHES", "WEIGHT_KG", "WEIGHT_POUNDS"].includes(question.uiRenderType)) && (
          <div
            className={`form-group ${validationErrors && validationErrors.some((error) => error.id === question.id) ? "hasError" : ""}`}
            ref={(el) => {
              const refId = question.uniqueId;
              inputRefs.current[refId] = el;
            }}
          >
            <input
              title={question.text}
              placeholder={stripHtmlTags(question.defaultText)}
              className="form-control"
              type="text"
              id={`${question.uniqueId}`}
              value={inputValue}  // Ensure value is 0 when question.value is 0
              onChange={(event) => handleNumericChangeUnits(event)}
              onBlur={handleBlurUnits}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();  // Prevent form submission
                  //handleBlurUnits(event);
                  event.target.blur();
                }
              }}
            />
            <div className={`invalid-feedback ${hasValidated && inputRefs.current[question.uniqueId] ? "d-block" : ""}`}>
              {validationErrors && validationErrors.find((error) => error.id === question.id)?.errorText}
            </div>
          </div>
        )}

        {(["BEN_ADD_REM_FIRST", "BEN_ADD_REM"].includes(question.uiRenderType)) && (
          <div
            className={
              question.value === 0
                ? "beneficiary-add"
                : question.value === 1
                  ? "beneficiary-remove"
                  : ""
            }
            style={{
              display:
                question.uiRenderType === "BEN_ADD_REM_FIRST" && question.value === 2
                  ? "none"
                  : "block",
            }}
          >
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();

                let newValue;
                if (question.value === 0) {
                  newValue = 1;
                } else if (question.value === 1) {
                  newValue = 0;
                } else {
                  newValue = question.value;
                }

                const updatedQuestionValue = {
                  ...question,
                  value: newValue,
                };

                handleInputBlur(updatedQuestionValue);
              }}
            >
              {
                question.possibleValues.find(
                  (val) => val.value === question.value
                )?.uiValue || ""
              }
            </a>
          </div>
        )}


        {["ADD_HOLDER"].includes(question.uiRenderType) && (
         <div
            className={
              question.value === 0 || question.value === null
                ? "beneficiary-add"
                : question.value === 1
                  ? "beneficiary-remove"
                  : ""
            } 
            
             ref={(el) => {
              const refId = question.uniqueId;
              inputRefs.current[refId] = el;
            }}
          >
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();

                let newValue;
                if (question.value === null || question.value === 0) {
                  newValue = 1;
                } else if (question.value === 1) {
                  newValue = 0;
                } else {
                  newValue = question.value;
                }

                const updatedQuestionValue = {
                  ...question,
                  value: newValue,
                };

                handleInputBlur(updatedQuestionValue);
              }}
            >
              {question.text || "" }
            </a>
          </div>
        )}

        {["REMOVE_HOLDER"].includes(question.uiRenderType) && (
         <div
            className={
              question.value === 0 || question.value === null
                ? "beneficiary-add remove-holder"
                : question.value === 1
                  ? "beneficiary-remove"
                  : ""
            }   
            
             ref={(el) => {
              const refId = question.uniqueId;
              inputRefs.current[refId] = el;
            }}
          >
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();

                let newValue;
                if (question.value === null || question.value === 0) {
                  newValue = 1;
                } else if (question.value === 1) {
                  newValue = 0;
                } else {
                  newValue = question.value;
                }

                const updatedQuestionValue = {
                  ...question,
                  value: newValue,
                };

                handleInputBlur(updatedQuestionValue);
              }}
            >
              {question.text || "" }
            </a>
          </div>
        )}


        {(["MOVE_RETURN_SIMPLE_TEXT_VALUE", "MOVE_RETURN", "MOVE_NEXT"].includes(question.uiRenderType)) && (
          <div className="move-container">
            {/* Display text and value only for MOVE_RETURN_SIMPLE_TEXT_VALUE */}
            {question.uiRenderType === "MOVE_RETURN_SIMPLE_TEXT_VALUE" && (
              <div className="move-next-simple-text-value">
                {question.text || ''} :{" "}
                <span className="simple-text-value">
                  {formatCurrency(question.value, language, false, 'MONEY-INT')}
                </span>
              </div>
            )}

            <div className="move-next">
              <div className="buttons-results check">
                <div className="form-check radio-binary">
                  <input
                    className="form-check-input"
                    type="radio"
                    id={question.uniqueId}
                    name={question.uniqueId}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleNavigateToQuestion(question.code, question.uiRenderType, question.compositionType);
                      }
                    }}
                  />
                  <label
                    className="form-check-label binary"
                    htmlFor={question.uniqueId}
onClick={(e) => {
  e.preventDefault();
  handleNavigateToQuestion(question.code, question.uiRenderType, question.compositionType);
}}
                  >
                    {t('modify')}
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}



        {savingQuestionId === question.uniqueId && (

          <div className="loading-dots-wrapper">
            <div className="loading-dots loading-0"></div>
            <div className="loading-dots loading-1"></div>
            <div className="loading-dots loading-2"></div>
          </div>

        )}

        {validationErrors
          .filter((error) => error.id === question.id && error.errorTitle)
          .map((error, index) => {
            const key = `${error.additionalInfoId || error.id}-${index}`;
            const { erroruiRenderType, errorTitle, errorContent } = error;

            switch (erroruiRenderType) {
              case "BLOCK":
              case "VALIDATION_AT_LEAST_ONE":
                return (
                  <div key={key} className="error-message active">
                    <h4>{errorTitle}</h4>
                    <p>{errorContent}</p>
                  </div>
                );

              case "VALIDATION":
                return (
                  <div key={key} className="invalid-feedback d-block">
                    {errorContent}
                  </div>
                );

              default:
                return null;
            }
          })}

        <Modal
          isVisible={isModalVisible}
          title={modalContent.title}
          content={modalContent.content}
          onClose={closeModal}
        />
      </div>

      {isDebugMode() && <div className="dev-id">ID: {question.uniqueId}</div>}

    </>
  )
};

export default Questions;